/**
 * Arabic font support for jsPDF (Noto Naskh Arabic).
 */

const FONT_REGULAR_FILE = 'NotoNaskhArabic-Regular.ttf';
const FONT_BOLD_FILE = 'NotoNaskhArabic-Bold.ttf';
export const ARABIC_PDF_FONT = 'NotoArabic';

const REGULAR_URLS = [
  `${process.env.PUBLIC_URL || ''}/fonts/${FONT_REGULAR_FILE}`,
  'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoNaskhArabic/NotoNaskhArabic-Regular.ttf'
];

const BOLD_URLS = [
  `${process.env.PUBLIC_URL || ''}/fonts/${FONT_BOLD_FILE}`,
  'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoNaskhArabic/NotoNaskhArabic-Bold.ttf'
];

let regularBase64 = null;
let boldBase64 = null;
let loadPromise = null;

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function fetchFirstAvailable(urls) {
  let lastError;
  for (const url of urls) {
    if (!url) continue;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        lastError = new Error(`${url} (${response.status})`);
        continue;
      }
      return arrayBufferToBase64(await response.arrayBuffer());
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('No font URL available');
}

async function loadFonts() {
  if (regularBase64 && boldBase64) {
    return { regularBase64, boldBase64 };
  }
  if (!loadPromise) {
    loadPromise = (async () => {
      regularBase64 = await fetchFirstAvailable(REGULAR_URLS);
      boldBase64 = await fetchFirstAvailable(BOLD_URLS);
      return { regularBase64, boldBase64 };
    })().catch((err) => {
      loadPromise = null;
      console.warn('[Arabic PDF] Could not load Arabic fonts:', err);
      return { regularBase64: null, boldBase64: null };
    });
  }
  return loadPromise;
}

function registerFontsOnDoc(doc, regular, bold) {
  const list = doc.getFontList?.() || {};
  if (!list[ARABIC_PDF_FONT]) {
    doc.addFileToVFS(FONT_REGULAR_FILE, regular);
    doc.addFileToVFS(FONT_BOLD_FILE, bold);
    doc.addFont(FONT_REGULAR_FILE, ARABIC_PDF_FONT, 'normal');
    doc.addFont(FONT_BOLD_FILE, ARABIC_PDF_FONT, 'bold');
  }
}

/**
 * Register Arabic font and enable RTL on jsPDF document.
 * @param {import('jspdf').jsPDF} doc
 * @returns {Promise<boolean>}
 */
export async function applyArabicPdfFont(doc) {
  const { regularBase64: reg, boldBase64: bold } = await loadFonts();
  if (!reg || !bold) return false;

  registerFontsOnDoc(doc, reg, bold);
  doc.setFont(ARABIC_PDF_FONT, 'normal');
  // Do not use setR2L — it shifts autoTable left and clips Arabic text.
  // RTL is handled via halign: 'right' and letterhead layout instead.

  return true;
}

export const REPORT_ARABIC_HTML_FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
  body, table, th, td, h1, h2, h3, p, span, div {
    font-family: 'Cairo', 'Noto Naskh Arabic', Arial, sans-serif !important;
  }
`;
